// 조직도 — 좌: 간결한 조직 구조(부서 드래그 재배치) · 우: 전체 구성원(부서로 드래그 이동).
// 모든 변경은 백엔드에서 자동으로 역할(등급·권한)에 반영된다.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../../shared/api/client";
import { Loading } from "../../../shared/components/Empty";
import { toast } from "../../../shared/stores/toasts";
import { ContextMenu, type CtxItem } from "../../../shell/SubNav";

type DeptNode = { id: number; code: string; name: string; name_en: string | null; level: number; manager_id: number | null; children: DeptNode[] };
type Member = {
  emp_id: number; user_id: number; name: string; department_id: number;
  position_id: number | null; position_name: string | null;
  job_title_id: number | null; job_title_name: string | null;
  org_role: string | null; is_dept_manager: boolean;
};
type Opt = { id: number; name: string };
type Drag = { type: "dept" | "member"; id: number } | null;

const ROLE_LABEL: Record<string, string> = { CEO: "대표", C_LEVEL: "임원", DIVISION_HEAD: "실장", TEAM_LEAD: "팀장", MANAGER: "부서장", EMPLOYEE: "사원" };
const ROLE_COLOR: Record<string, string> = { CEO: "#9333ea", C_LEVEL: "#7c3aed", DIVISION_HEAD: "#dc2626", TEAM_LEAD: "#2563eb", MANAGER: "#0891b2" };
const btn: React.CSSProperties = { fontSize: 11, padding: "1px 6px", lineHeight: 1.4 };
const errMsg = (e: any) => e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "";

export function AdminDepartments() {
  const [tree, setTree] = useState<DeptNode[] | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [positions, setPositions] = useState<Opt[]>([]);
  const [jobTitles, setJobTitles] = useState<Opt[]>([]);
  const [drag, setDrag] = useState<Drag>(null);
  const [dropDept, setDropDept] = useState<number | "root" | null>(null);
  const [selDept, setSelDept] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [editMember, setEditMember] = useState<Member | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number; items: CtxItem[] } | null>(null);
  const [deptModal, setDeptModal] = useState<{ mode: "add" | "edit"; parentId: number | null; dept: DeptNode | null } | null>(null);

  const reload = useCallback(() => {
    api.get<DeptNode[]>("/iam/departments/tree").then(r => setTree(r.data)).catch(() => setTree([]));
    api.get<Member[]>("/iam/org/members").then(r => setMembers(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    reload();
    api.get<Opt[]>("/iam/positions").then(r => setPositions(r.data)).catch(() => {});
    api.get<Opt[]>("/iam/job-titles").then(r => setJobTitles(r.data)).catch(() => {});
  }, [reload]);

  const deptList = useMemo(() => {
    const out: DeptNode[] = [];
    const walk = (ns: DeptNode[]) => ns.forEach(n => { out.push(n); walk(n.children); });
    walk(tree || []);
    return out;
  }, [tree]);
  const countOf = (id: number) => members.filter(m => m.department_id === id).length;
  const nameOf = (id: number) => deptList.find(d => d.id === id)?.name ?? "—";
  const mgrOf = (id: number) => deptList.find(d => d.id === id)?.manager_id ?? null;

  const moveMember = async (empId: number, deptId: number) => {
    const m = members.find(x => x.emp_id === empId);
    if (!m || m.department_id === deptId) return;
    try { await api.patch(`/iam/employees/${empId}`, { department_id: deptId }); toast.success(`${m.name} → ${nameOf(deptId)} 이동 (권한 자동 반영)`); reload(); }
    catch (e) { toast.error("이동 실패", errMsg(e)); }
  };
  const moveDept = async (deptId: number, parentId: number | null) => {
    try { await api.patch(`/iam/departments/${deptId}`, { parent_id: parentId }); toast.success("부서 이동됨"); reload(); }
    catch (e) { toast.error("부서 이동 실패", errMsg(e)); }
  };
  const setManager = async (deptId: number, userId: number | null) => {
    try { await api.patch(`/iam/departments/${deptId}`, { manager_id: userId }); toast.success(userId ? "부서장 임명 (권한 자동 반영)" : "부서장 해제"); reload(); }
    catch (e) { toast.error("부서장 변경 실패", errMsg(e)); }
  };
  const saveMemberPos = async (m: Member, pos: number | null, jt: number | null) => {
    try { await api.patch(`/iam/employees/${m.emp_id}`, { position_id: pos, job_title_id: jt }); toast.success("직급/직책 변경 (권한 자동 반영)"); setEditMember(null); reload(); }
    catch (e) { toast.error("변경 실패", errMsg(e)); }
  };
  const syncRoles = async () => {
    setSyncing(true);
    try { const r = await api.post<{ synced: number }>("/iam/org/sync-roles"); toast.success(`권한 재계산 완료 — ${r.data.synced}명`); reload(); }
    catch (e) { toast.error("동기화 실패", errMsg(e)); } finally { setSyncing(false); }
  };
  const openAdd = (parentId: number | null) => setDeptModal({ mode: "add", parentId, dept: null });
  const openEdit = (n: DeptNode) => setDeptModal({ mode: "edit", parentId: null, dept: n });
  const saveDept = async (v: { name: string; name_en: string; code: string }) => {
    const m = deptModal;
    if (!m) return;
    try {
      if (m.mode === "add") {
        await api.post("/iam/departments", { name: v.name, name_en: v.name_en || null, code: v.code, parent_id: m.parentId });
        toast.success("부서 추가됨");
      } else if (m.dept) {
        await api.patch(`/iam/departments/${m.dept.id}`, { name: v.name, name_en: v.name_en || null });
        toast.success("부서 수정됨");
      }
      setDeptModal(null);
      reload();
    } catch (e) { toast.error("저장 실패", errMsg(e)); }
  };
  const delDept = async (n: DeptNode) => {
    if (countOf(n.id) || n.children.length) { toast.error("비어있는 부서만 삭제할 수 있습니다"); return; }
    if (!confirm(`"${n.name}" 부서를 삭제하시겠습니까?`)) return;
    try { await api.delete(`/iam/departments/${n.id}`); reload(); } catch (e) { toast.error("삭제 실패", errMsg(e)); }
  };

  const deptCtx = (n: DeptNode): CtxItem[] => [
    { label: "하위 부서 추가", icon: "📁", onClick: () => openAdd(n.id) },
    { label: "부서 수정 (이름·영문)", icon: "✏️", onClick: () => openEdit(n) },
    { label: "최상위로 이동", icon: "🏠", onClick: () => moveDept(n.id, null) },
    { sep: true },
    { label: "부서 삭제", icon: "🗑️", onClick: () => delDept(n), danger: true },
  ];
  const memberCtx = (m: Member): CtxItem[] => {
    const isMgr = mgrOf(m.department_id) === m.user_id;
    return [
      { label: "직급 / 직책 편집", icon: "⚙️", onClick: () => setEditMember(m) },
      { label: isMgr ? "부서장 해제" : "이 부서의 부서장으로 임명", icon: "👑", onClick: () => setManager(m.department_id, isMgr ? null : m.user_id) },
    ];
  };
  const openCtx = (e: React.MouseEvent, items: CtxItem[]) => {
    e.preventDefault(); e.stopPropagation();
    setCtx({ x: e.clientX, y: e.clientY, items });
  };

  if (tree === null) return <Loading />;

  const onDropDept = (deptId: number) => {
    setDropDept(null);
    if (!drag) return;
    if (drag.type === "member") moveMember(drag.id, deptId);
    else if (drag.type === "dept" && drag.id !== deptId) moveDept(drag.id, deptId);
    setDrag(null);
  };

  const renderDept = (n: DeptNode, depth: number) => {
    const active = dropDept === n.id;
    const sel = selDept === n.id;
    const mgr = n.manager_id ? members.find(m => m.user_id === n.manager_id) : null;
    return (
      <div key={n.id}>
        <div draggable
          onDragStart={e => { e.stopPropagation(); setDrag({ type: "dept", id: n.id }); }}
          onDragEnd={() => setDrag(null)}
          onDragOver={e => { e.preventDefault(); setDropDept(n.id); }}
          onDragLeave={() => setDropDept(d => (d === n.id ? null : d))}
          onDrop={e => { e.preventDefault(); e.stopPropagation(); onDropDept(n.id); }}
          onClick={() => setSelDept(s => (s === n.id ? null : n.id))}
          onContextMenu={e => openCtx(e, deptCtx(n))}
          style={{
            display: "flex", alignItems: "center", gap: 7, padding: "5px 8px", paddingLeft: 8 + depth * 18,
            borderRadius: 7, cursor: "grab", marginBottom: 1,
            background: active ? "rgba(99,102,241,0.14)" : sel ? "rgba(99,102,241,0.08)" : "transparent",
            outline: active ? "1.5px dashed var(--p-500,#6366f1)" : "none",
          }}>
          <span style={{ fontSize: 13 }}>📁</span>
          <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text-1)" }}>{n.name}</span>
          {n.name_en && <span style={{ fontSize: 10.5, color: "var(--text-3)", fontStyle: "italic" }}>{n.name_en}</span>}
          <span style={{ fontSize: 10, color: "var(--text-3)" }}>L{n.level}</span>
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>· {countOf(n.id)}명</span>
          {mgr && <span style={{ fontSize: 11, color: "#b45309" }} title="부서장">👑{mgr.name}</span>}
          <div style={{ marginLeft: "auto", display: "flex", gap: 2 }} onClick={e => e.stopPropagation()}>
            <button className="t-btn" style={btn} title="하위 부서 추가" onClick={() => openAdd(n.id)}>＋</button>
            <button className="t-btn" style={btn} title="부서 수정 (이름·영문)" onClick={() => openEdit(n)}>✏</button>
            <button className="t-btn" style={btn} title="삭제" onClick={() => delDept(n)}>🗑</button>
          </div>
        </div>
        {n.children.map(c => renderDept(c, depth + 1))}
      </div>
    );
  };

  const shown = members.filter(m =>
    (selDept == null || m.department_id === selDept) &&
    (!search || m.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      {/* LEFT — org structure */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: 15 }}>조직 구조</h2>
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>부서를 드래그해 상·하위 이동 · 부서 클릭 시 우측 필터</span>
          <button className="t-btn t-btn-primary" style={{ marginLeft: "auto" }} disabled={syncing} onClick={syncRoles}>
            {syncing ? "동기화 중…" : "🔄 권한 재계산"}
          </button>
        </div>
        <div className="t-card" style={{ padding: 8 }}>
          <div
            onDragOver={e => { if (drag?.type === "dept") { e.preventDefault(); setDropDept("root"); } }}
            onDragLeave={() => setDropDept(d => (d === "root" ? null : d))}
            onDrop={e => { e.preventDefault(); setDropDept(null); if (drag?.type === "dept") moveDept(drag.id, null); setDrag(null); }}
            style={{
              display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--text-3)",
              padding: "4px 8px", borderRadius: 6, marginBottom: 4,
              background: dropDept === "root" ? "rgba(99,102,241,0.12)" : "transparent",
              outline: dropDept === "root" ? "1.5px dashed var(--p-500,#6366f1)" : "none",
            }}>
            🏠 최상위 — 여기로 드래그하면 루트 부서로 이동
            <button className="t-btn" style={{ ...btn, marginLeft: "auto" }} onClick={() => openAdd(null)}>＋ 루트 부서</button>
          </div>
          {tree.map(n => renderDept(n, 0))}
          {tree.length === 0 && <div style={{ padding: 16, textAlign: "center", color: "var(--text-3)" }}>부서가 없습니다 — ＋루트 부서로 추가</div>}
        </div>
      </div>

      {/* RIGHT — member list */}
      <div style={{ width: 300, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 15 }}>전체 구성원</h2>
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>({shown.length})</span>
          {selDept != null && (
            <button className="t-btn" style={{ ...btn, marginLeft: "auto" }} onClick={() => setSelDept(null)}>✕ {nameOf(selDept)}</button>
          )}
        </div>
        <input className="t-input" placeholder="이름 검색…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 8, width: "100%", boxSizing: "border-box" }} />
        <div className="t-card" style={{ padding: 6, maxHeight: "calc(100vh - 240px)", overflowY: "auto" }}>
          {shown.map(m => (
            <div key={m.emp_id} draggable
              onDragStart={() => setDrag({ type: "member", id: m.emp_id })} onDragEnd={() => setDrag(null)}
              onClick={() => setEditMember(m)} onContextMenu={e => openCtx(e, memberCtx(m))}
              title="드래그: 부서 이동 · 클릭: 편집 · 우클릭: 메뉴"
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 8px", borderRadius: 7, cursor: "grab", borderBottom: "1px solid var(--border)" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {m.name}{m.is_dept_manager && " 👑"}
                </div>
                <div style={{ fontSize: 10.5, color: "var(--text-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {nameOf(m.department_id)} · {m.job_title_name || m.position_name || "—"}
                </div>
              </div>
              {m.org_role && (
                <span style={{ fontSize: 9, fontWeight: 700, color: "#fff", background: ROLE_COLOR[m.org_role] ?? "#6b7280", padding: "1px 6px", borderRadius: 8, flexShrink: 0 }}>
                  {ROLE_LABEL[m.org_role] ?? m.org_role}
                </span>
              )}
            </div>
          ))}
          {shown.length === 0 && <div style={{ padding: 16, textAlign: "center", color: "var(--text-3)", fontSize: 12 }}>구성원 없음</div>}
        </div>
      </div>

      {editMember && (
        <MemberEdit m={editMember} positions={positions} jobTitles={jobTitles}
          deptName={nameOf(editMember.department_id)}
          isMgrHere={mgrOf(editMember.department_id) === editMember.user_id}
          onSetManager={() => { const me = editMember; const isMgr = mgrOf(me.department_id) === me.user_id; setManager(me.department_id, isMgr ? null : me.user_id); setEditMember(null); }}
          onSave={saveMemberPos} onClose={() => setEditMember(null)} />
      )}
      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={ctx.items} onClose={() => setCtx(null)} />}
      {deptModal && (
        <DeptEditModal mode={deptModal.mode} dept={deptModal.dept}
          onSave={saveDept} onClose={() => setDeptModal(null)} />
      )}
    </div>
  );
}

function DeptEditModal({ mode, dept, onSave, onClose }: {
  mode: "add" | "edit"; dept: DeptNode | null;
  onSave: (v: { name: string; name_en: string; code: string }) => void; onClose: () => void;
}) {
  const [name, setName] = useState(dept?.name ?? "");
  const [nameEn, setNameEn] = useState(dept?.name_en ?? "");
  const [code, setCode] = useState(dept?.code ?? "");
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "block", marginBottom: 4 };
  const valid = !!name.trim() && (mode === "edit" || !!code.trim());
  const submit = () => { if (valid) onSave({ name: name.trim(), name_en: nameEn.trim(), code: code.trim() }); };
  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 360, maxWidth: "95vw" }}>
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>{mode === "add" ? "＋ 부서 추가" : "✏️ 부서 수정"}</h3>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div>
            <label style={lbl}>부서명 *</label>
            <input className="t-input" autoFocus value={name} onChange={e => setName(e.target.value)}
              placeholder="예: 기술본부" onKeyDown={e => { if (e.key === "Enter") submit(); }} />
          </div>
          <div>
            <label style={lbl}>영문 부서명 <span style={{ fontWeight: 400 }}>(선택 — 부서명 옆에 회색으로 표시)</span></label>
            <input className="t-input" value={nameEn} onChange={e => setNameEn(e.target.value)}
              placeholder="예: Tech Division" onKeyDown={e => { if (e.key === "Enter") submit(); }} />
          </div>
          {mode === "add" ? (
            <div>
              <label style={lbl}>부서 코드 * <span style={{ fontWeight: 400 }}>(영문/숫자, 고유)</span></label>
              <input className="t-input" value={code}
                onChange={e => setCode(e.target.value.replace(/[^A-Za-z0-9_]/g, "").toUpperCase())}
                placeholder="예: TECH" style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }} />
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "var(--text-3)" }}>부서 코드 <code>{dept?.code}</code> 는 변경할 수 없습니다.</div>
          )}
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose}>취소</button>
          <button className="t-btn t-btn-primary" disabled={!valid} onClick={submit}>{mode === "add" ? "추가" : "저장"}</button>
        </div>
      </div>
    </div>
  );
}

function MemberEdit({ m, positions, jobTitles, deptName, isMgrHere, onSetManager, onSave, onClose }: {
  m: Member; positions: Opt[]; jobTitles: Opt[]; deptName: string; isMgrHere: boolean;
  onSetManager: () => void; onSave: (m: Member, pos: number | null, jt: number | null) => void; onClose: () => void;
}) {
  const [pos, setPos] = useState<string>(m.position_id != null ? String(m.position_id) : "");
  const [jt, setJt] = useState<string>(m.job_title_id != null ? String(m.job_title_id) : "");
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "block", marginBottom: 4 };
  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 350, maxWidth: "95vw" }}>
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>{m.name}</h3>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{deptName}</div>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div>
            <label style={lbl}>직급 (사원·대리·…·이사)</label>
            <select className="t-input" value={pos} onChange={e => setPos(e.target.value)}>
              <option value="">(없음)</option>
              {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>직책 (팀원·팀장·본부장)</label>
            <select className="t-input" value={jt} onChange={e => setJt(e.target.value)}>
              <option value="">(없음)</option>
              {jobTitles.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
            </select>
          </div>
          <button className="t-btn" onClick={onSetManager}
            style={{ justifyContent: "flex-start", border: isMgrHere ? "1.5px solid #f59e0b" : undefined, background: isMgrHere ? "rgba(245,158,11,0.08)" : undefined }}>
            👑 {isMgrHere ? "이 부서의 부서장 — 해제" : "이 부서의 부서장으로 임명"}
          </button>
          <div style={{ fontSize: 11, color: "var(--text-3)", lineHeight: 1.5 }}>
            이사→임원, 본부장/실장→실장, 팀장→팀장 등급이 자동 부여되며 전체 권한에 반영됩니다.
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose}>취소</button>
          <button className="t-btn t-btn-primary" onClick={() => onSave(m, pos === "" ? null : Number(pos), jt === "" ? null : Number(jt))}>저장</button>
        </div>
      </div>
    </div>
  );
}
